import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { reviewsAPI } from '../lib/api';

/** Cache in-memory por sessão. Reduz chamadas repetidas. */
const _cache = new Map(); // key: `${type}:${id}` -> {avg, count}

/**
 * Hook — busca avg/count em lote para vários alvos numa chamada.
 * targets = [{ target_type, target_id }, ...]
 */
export const useBulkRatings = (targets) => {
  const [map, setMap] = useState({});
  const signature = JSON.stringify((targets || []).map((t) => `${t.target_type}:${t.target_id}`).sort());

  useEffect(() => {
    if (!targets || targets.length === 0) return;
    // filtra o que já está em cache
    const missing = targets.filter((t) => t.target_id && !_cache.has(`${t.target_type}:${t.target_id}`));
    const cached = {};
    targets.forEach((t) => {
      const k = `${t.target_type}:${t.target_id}`;
      if (_cache.has(k)) cached[k] = _cache.get(k);
    });
    setMap(cached);
    if (missing.length === 0) return;
    reviewsAPI
      .bulkSummary(missing.map((t) => ({ target_type: t.target_type, target_id: t.target_id })))
      .then(({ data }) => {
        const summaries = data.summaries || {};
        Object.entries(summaries).forEach(([k, v]) => _cache.set(k, v));
        missing.forEach((t) => {
          const k = `${t.target_type}:${t.target_id}`;
          if (!_cache.has(k)) _cache.set(k, { avg: 0, count: 0 });
        });
        // gera novo map completo
        const complete = {};
        targets.forEach((t) => {
          const k = `${t.target_type}:${t.target_id}`;
          complete[k] = _cache.get(k) || { avg: 0, count: 0 };
        });
        setMap(complete);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return map;
};

/**
 * Selo de nota profissional — mostra estrela + média + total.
 * Retorna null quando não há avaliações (evita ruído em produtos novos).
 */
export const RatingBadge = ({ value = 0, count = 0, size = 'sm', showZero = false }) => {
  const c = Number(count) || 0;
  const v = Number(value) || 0;
  if (c === 0 && !showZero) return null;
  const sizes = {
    xs: 'text-[10px] px-1.5 py-0.5 gap-0.5',
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-1 gap-1',
    lg: 'text-base px-3 py-1.5 gap-1.5',
  };
  const iconSize = { xs: 10, sm: 12, md: 14, lg: 16 }[size] || 12;
  return (
    <span
      className={`inline-flex items-center rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-semibold ${sizes[size]}`}
      data-testid="rating-badge"
      title={`${v.toFixed(1)} de 5 (${c} ${c === 1 ? 'avaliação' : 'avaliações'})`}
    >
      <Star width={iconSize} height={iconSize} className="fill-current" />
      {c > 0 ? v.toFixed(1) : '—'}
      <span className="text-amber-600/70 font-medium">({c})</span>
    </span>
  );
};

export default RatingBadge;
